import React from "react";
import {
  Box,
  Flex,
  FormLabel,
  Input,
  Select,
  Alert, AlertIcon, Text, VStack
} from "components-lib";

/**
 * PatientFormFields Component
 * Contains all the form fields for patient registration
 * Handles form layout and field validation display
 */
const PatientFormFields = ({
  patientData = {},
  onInputChange,
  fieldErrors = {}
}) => {
  // Normalize legacy keys to current shape
  const normalizePatient = (p = {}) => {
    const out = {};

    // helpers
    const toStr = v => (v === null || v === undefined ? "" : String(v));
    const toDateISO = v => {
      if (!v && v !== 0) return "";
      // already ISO-like
      if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
      const num = Number(v);
      if (!Number.isNaN(num)) {
        const d = new Date(num);
        if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      }
      // fallback
      try {
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      } catch {
        return "";
      }
      return "";
    };

    // map common legacy keys to canonical keys
    out.patient_code = p.patient_code ?? p.patientCode ?? p.code ?? p.id ?? "";
    out.name = p.name ?? p.full_name ?? p.fullName ?? "";
    // gender normalization (legacy might use "M"/"F" or capitalized)
    const rawGender = (p.gender ?? p.sex ?? "").toString().toLowerCase();
    if (rawGender === "m" || rawGender === "male") out.gender = "male";
    else if (rawGender === "f" || rawGender === "female") out.gender = "female";
    else out.gender = rawGender || "";

    // age might be number or string
    out.age = p.age ?? (p.years && !isNaN(Number(p.years)) ? Number(p.years) : "");

    // dob may be timestamp or string
    out.dob = toDateISO(p.dob ?? p.date_of_birth ?? p.dateOfBirth ?? "");

    out.email = p.email ?? p.emailAddress ?? p.email_address ?? "";
    out.phoneNumber = toStr(p.phoneNumber ?? p.phone_number ?? p.phone ?? p.mobile ?? "");
    out.address = p.address ?? p.addr ?? "";
    out.city = p.city ?? p.town ?? "";
    out.pincode = toStr(p.pincode ?? p.pin ?? p.zip ?? "");
    out.language = (p.language ?? p.lang ?? "").toString().toLowerCase();
    out.bloodgroup = p.bloodgroup ?? p.blood_group ?? p.bloodGroup ?? "";
    out.existingId = p.existingId ?? p.existingId ?? p.old_id ?? null;
    out.profile_photo = p.profile_photo ?? p.profilePhoto ?? p.photo ?? null;
    // out.patient_id = p.patient_id ?? p.patientId ?? p.id ?? null;

    return out;
  };

  // Create normalized display and editable state so legacy input is shown & editable
  const normalizedFromProps = React.useMemo(() => normalizePatient(patientData), [patientData]);
  const [formState, setFormState] = React.useState(normalizedFromProps);

  // keep local state in sync when parent patientData changes
  React.useEffect(() => {
    setFormState(normalizedFromProps);
  }, [normalizedFromProps]);

  // helper to map fieldErrors: checks canonical key and some legacy variants
  const getFieldError = (key) => {
    if (!fieldErrors) return undefined;
    // check canonical key first
    if (fieldErrors[key]) return fieldErrors[key];
    // common legacy alternatives
    const altMap = {
      phoneNumber: ["phone_number", "phone", "mobile"],
      pincode: ["pin", "zip"],
      bloodgroup: ["blood_group", "bloodGroup"],
      patient_code: ["patientCode", "code"],
      // patient_id: ["patientId", "id"]
    };
    const alts = altMap[key] ?? [];
    for (const a of alts) {
      if (fieldErrors[a]) return fieldErrors[a];
    }
    return undefined;
  };

  // wrapper change handler to keep local formState and call parent's onInputChange with canonical names
  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, type } = e.target;
    let value;
    if (type === "file") {
      value = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    } else {
      value = e.target.value;
    }

    setFormState(prev => {
      const next = { ...prev, [name]: value };
      return next;
    });

    // call parent's onInputChange if provided, emulate a normal event
    if (typeof onInputChange === "function") {
      onInputChange({
        target: {
          name,
          value
        }
      });
    }
  };

  // Map backend keys to form field keys and labels (keeps same structure as before)
  // decide requirement between age and dob:
  // - if either age or dob is provided, neither is required
  // - if neither is provided, require only "age" (prefer age as the single required field)
  const ageProvided = formState.age !== "" && formState.age !== null && formState.age !== undefined;
  const dobProvided = formState.dob !== "" && formState.dob !== null && formState.dob !== undefined;

  const ageRequired = !ageProvided && !dobProvided;
  const dobRequired = false;

  const fieldMap = [
    { key: "patient_code", label: "Patient Code", readOnly: true },
    { key: "name", label: "Name *", required: true },
    {
      key: "gender",
      label: "Gender *",
      type: "select",
      options: [
        { value: "male", label: "Male" },
        { value: "female", label: "Female" }
      ],
      required: true
    },
    { key: "age", label: "Age", required: ageRequired },
    { key: "dob", label: "DOB", type: "date", required: dobRequired },
    { key: "phoneNumber", label: "Phone *", type: "tel", required: true },
    { key: "email", label: "Email", type: "email" },
    { key: "address", label: "Address" },
    { key: "city", label: "City" },
    { key: "pincode", label: "Pin" },
    {
      key: "language",
      label: "Language",
      type: "select",
      options: [
        { value: "english", label: "English" },
        { value: "hindi", label: "Hindi" },
        { value: "bengali", label: "Bengali" },
        { value: "marathi", label: "Marathi" },
        { value: "telugu", label: "Telugu" },
        { value: "tamil", label: "Tamil" },
        { value: "gujarati", label: "Gujarati" },
        { value: "assamese", label: "Assamese" },
        { value: "kannada", label: "Kannada" },
        { value: "malayalam", label: "Malayalam" },
        { value: "punjabi", label: "Punjabi" },
      ]
    },
    {
      key: "bloodgroup",
      label: "Blood Group",
      type: "select",
      options: [
        { value: "A+", label: "A+" },
        { value: "A-", label: "A-" },
        { value: "B+", label: "B+" },
        { value: "B-", label: "B-" },
        { value: "AB+", label: "AB+" },
        { value: "AB-", label: "AB-" },
        { value: "O+", label: "O+" },
        { value: "O-", label: "O-" },
      ]
    },
    { key: "existingId", label: "Existing ID" },
    { key: "profile_photo", label: "Profile Photo", type: "file", hidden: true },
    // { key: "patient_id", label: "Patient ID", readOnly: true }
  ];

  // map of key -> clean label (remove trailing asterisk)
  const labelByKey = fieldMap.reduce((acc, f) => {
    acc[f.key] = (f.label || "").replace(/\s*\*$/, "");
    return acc;
  }, {});

  // Validate required fields (returns array of missing keys)
  const validateRequiredFields = () => {
    const missing = fieldMap
      .filter(f => f.required && !f.hidden)
      .map(f => f.key)
      .filter(key => {
        const val = formState[key];
        // treat empty string, null, undefined as missing
        return val === "" || val === null || val === undefined;
      });
    return missing;
  };

  // Helper to notify user of missing required fields.
  // opts.toast: pass Chakra's toast function from parent to show a toast.
  // If toast not provided, falls back to window.alert.
  const notifyMissingFields = (missingKeys = [], opts = {}) => {
    if (!missingKeys || missingKeys.length === 0) return;
    // If both age and dob are missing, show a combined message "Age or DOB"
    const missingSet = new Set(missingKeys);
    const labels = [];

    if (missingSet.has("age") && missingSet.has("dob")) {
      labels.push("Age or DOB");
      missingSet.delete("age");
      missingSet.delete("dob");
    }

    for (const k of Array.from(missingSet)) {
      labels.push(labelByKey[k] ?? k);
    }

    const message = `Please fill required fields: ${labels.join(", ")}`;

    if (opts && typeof opts.toast === "function") {
      // Chakra toast usage by caller (EditPatientForm should pass its toast)
      opts.toast({
        title: "Validation error",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else if (typeof window !== "undefined" && typeof window.alert === "function") {
      window.alert(message);
    }
  };

  // Expose a simple global helper so parent (or EditPatientForm) can call before submitting.
  // Preferred usage from EditPatientForm:
  // const ok = window.patientFormValidateBlocking({ toast });
  // if (!ok) return; // abort API call
  const validateAndNotify = (opts = {}) => {
    const missing = validateRequiredFields();
    if (missing.length > 0) {
      notifyMissingFields(missing, opts);
    }
    return missing;
  };

  const validateBlocking = (opts = {}) => {
    const missing = validateRequiredFields();
    if (missing.length > 0) {
      notifyMissingFields(missing, opts);
      return false;
    }
    return true;
  };

  if (typeof window !== "undefined") {
    window.patientFormValidate = validateAndNotify; // returns array of missing keys, also notifies if opts.toast provided
    window.patientFormValidateBlocking = validateBlocking; // returns boolean: true = valid, false = invalid (and notifies)
  }

  // Group fields for layout (3 per row)
  const groupedFields = [];
  for (let i = 0; i < fieldMap.length; i += 2) {
    groupedFields.push(fieldMap.slice(i, i + 2));
  }

  return (
    <>
      {groupedFields.map((fields, idx) => (
        <Flex mb={2} gap={2} key={idx}>
          {fields.map(field => {
            if (field.hidden) return null;
            if (field.type === "select") {
              return (
                <Box flex="1" key={field.key}>
                  <FormLabel>{field.label}</FormLabel>
                  <Select
                    placeholder={field.label}
                    name={field.key}
                    value={formState[field.key] ?? ""}
                    onChange={handleChange}
                    isInvalid={!!getFieldError(field.key)}
                    required={field.required}
                    readOnly={field.readOnly}
                  >
                    {field.options.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Select>
                </Box>
              );
            }
            return (
              <Box flex="1" key={field.key}>
                <FormLabel>{field.label}</FormLabel>
                <Input
                  placeholder={field.label}
                  name={field.key}
                  type={field.type || "text"}
                  value={field.type === "file" ? undefined : (formState[field.key] ?? "")}
                  onChange={handleChange}
                  isInvalid={!!getFieldError(field.key)}
                  required={field.required}
                  readOnly={field.readOnly}
                />
              </Box>
            );
          })}
        </Flex>
      ))}
      {/* DEBUG TOOL */}
      {/* <Alert status="info" mb={4} flexDirection="column" alignItems="flex-start">
        <AlertIcon />
        <Box>
          <Text fontWeight="semibold" mb={2}>
            Original patient fields (as received):
          </Text>
          <VStack align="stretch" spacing={1} mb={3}>
            {Object.entries(patientData ?? {}).map(([key, val]) => (
              <Text key={key}>
                <Text as="span" fontWeight="bold" mr={2}>
                  {key}:
                </Text>
                <Text as="span">{val === null ? "null" : String(val)}</Text>
              </Text>
            ))}
          </VStack>

          <Text fontWeight="semibold" mb={2}>
            Normalized patient fields (editable):
          </Text>
          <VStack align="stretch" spacing={1}>
            {Object.entries(formState).map(([key, val]) => (
              <Text key={key}>
                <Text as="span" fontWeight="bold" mr={2}>
                  {key}:
                </Text>
                <Text as="span">{val === null ? "null" : String(val)}</Text>
              </Text>
            ))}
          </VStack>
        </Box>
      </Alert> */}
    </>
  );
};
// </Text ></Text ></VStack ></Text ></Text ></VStack ></Text ></Alert ></Box ></Select ></Box ></Flex ></>

export default PatientFormFields;